import { Grid, Box } from "@mui/material";
import * as MUIIcon from "@mui/icons-material";
import React, { useState, useEffect } from "react";
import { Global_Data } from "../../globalData/GlobalData";
import { serverAddress } from "../../config";
import ComponentLoadingBoundary from "../Loading/ComponentLoadingBoundary";
import usePendingRequests from "../Loading/usePendingRequests";

function replacePlaceholders(url, values) {
  return url?.replace(/\{(\w+)\}/g, (_, key) => values?.[key] ?? "");
}

function hasUnresolvedPlaceholders(value) {
  return value?.includes("{") || value?.includes("}");
}

function getPlaceholderKeys(value) {
  const matches = value?.match(/\{(\w+)\}/g) || [];
  return matches.map((item) => item.replace("{", "").replace("}", ""));
}

function isDataAwareReady(sDataAware, values) {
  if (!sDataAware) return true;

  const keys = getPlaceholderKeys(sDataAware);

  if (!keys.length) return true;

  return keys.every((key) => {
    const value = values?.[key];
    return value !== undefined && value !== null && value !== "";
  });
}

function getNestedValue(obj, path) {
  return path?.split(".").reduce((acc, key) => acc?.[key], obj);
}

function VarImage({ data, varValue, listenerData }) {
  const { token, textValue } = Global_Data();

  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(false);
  const { loading, trackRequest } = usePendingRequests();

  const submode = data?.component?.options?.submode;
  const sAction = data?.data?.sAction;
  const sType = data?.data?.sType;
  const sDataSource = data?.data?.sDataSource;
  const sInfosource = data?.data?.sInfoSource;
  const sDataAware = data?.data?.sDataAware;

  const failedIconName =
    data?.component?.options?.sFailedIcon?.sIcon || "Wallpaper";

  const failedIconProps =
    data?.component?.options?.sFailedIcon?.sProps || {};

  const FallbackIcon = MUIIcon[failedIconName];

  const primaryContainer = data?.component?.options?.sPrimaryContainer;

  const combinedValues = { ...varValue, ...textValue };

  const dataAwareReady = isDataAwareReady(sDataAware, combinedValues);

  // sAction=DEFAULT, sType=Image
  useEffect(() => {
    if (sAction !== "DEFAULT" || sType !== "Image" || !sDataSource) return;

    if (!dataAwareReady) {
      setImgSrc(null);
      return;
    }

    const resolvedUrl = replacePlaceholders(sDataSource, combinedValues);

    if (!resolvedUrl || hasUnresolvedPlaceholders(resolvedUrl)) {
      setImgSrc(null);
      return;
    }

    setImgError(false);

    if (submode === "DEFAULT") {
      trackRequest(() => fetch(`${serverAddress}${resolvedUrl}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((res) => {
          if (!res.ok) throw new Error("fetch failed");
          return res.blob();
        })
        .then((blob) => {
          setImgSrc(URL.createObjectURL(blob));
        }))
        .catch(() => {
          setImgSrc(null);
          setImgError(true);
        });
    } else if (submode === "URL") {
      setImgSrc(resolvedUrl);
    }
  }, [
    sAction,
    sType,
    sDataSource,
    submode,
    token,
    dataAwareReady,
    JSON.stringify(combinedValues),
  ]);

  // sAction=JSON: use listenerData directly if provided, else fetch from URL
  useEffect(() => {
    if (sAction !== "JSON" || !sInfosource) return;

    if (!dataAwareReady) {
      setImgSrc(null);
      return;
    }

    if (submode !== "GROUPDATA") {
      if (submode === "DEFAULT" && sType !== "Image" && sType !== "Base64")
        return;
      if (submode === "URL" && sType !== "Image") return;
    }

    const isUrl =
      sDataSource?.startsWith("http") || sDataSource?.startsWith("/");

    if (!isUrl && !listenerData) return;

    setImgError(false);

    if (submode === "GROUPDATA" || listenerData) {
      const value = getNestedValue(listenerData, sInfosource);

      if (!value) {
        setImgSrc(null);
        setImgError(true);
        return;
      }

      if (sType === "Base64") {
        setImgSrc(
          value.startsWith("data:") ? value : `data:image/*;base64,${value}`
        );
      } else {
        setImgSrc(value);
      }

      return;
    }

    if (!sDataSource) return;

    const resolvedUrl = replacePlaceholders(sDataSource, combinedValues);

    if (!resolvedUrl || hasUnresolvedPlaceholders(resolvedUrl)) {
      setImgSrc(null);
      return;
    }

    const fetchOptions =
      submode === "DEFAULT"
        ? { headers: { Authorization: `Bearer ${token}` } }
        : {};

    const endpoint =
      submode === "DEFAULT" ? `${serverAddress}${resolvedUrl}` : resolvedUrl;

    trackRequest(() => fetch(endpoint, fetchOptions)
      .then((res) => {
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        return res.json();
      })
      .then((json) => {
        const value = getNestedValue(json, sInfosource);

        if (!value) throw new Error("sInfosource not found in response");

        if (sType === "Image") {
          setImgSrc(value);
        } else if (sType === "Base64") {
          setImgSrc(
            value.startsWith("data:") ? value : `data:image/*;base64,${value}`
          );
        }
      }))
      .catch(() => {
        setImgSrc(null);
        setImgError(true);
      });
  }, [
    sAction,
    sType,
    sDataSource,
    sInfosource,
    submode,
    token,
    listenerData,
    dataAwareReady,
    JSON.stringify(combinedValues),
  ]);

  const showFallback = !imgSrc || imgError;

  const imageContent = showFallback ? (
    FallbackIcon ? (
      <FallbackIcon {...failedIconProps} />
    ) : null
  ) : (
    <img
      src={imgSrc}
      alt={data?.component?.sName}
      onError={() => setImgError(true)}
      {...data?.component?.sProps}
    />
  );

  const wrappedContent =
    primaryContainer?.sType === "BOX" ? (
      <Box {...primaryContainer?.sProps}>{imageContent}</Box>
    ) : (
      imageContent
    );

  return (
    <Grid item {...data?.grid_props}>
      <ComponentLoadingBoundary
        loading={loading}
        label={`Loading ${data?.component?.sName}`}
        minHeight={48}
        size={24}
      >
        {wrappedContent}
      </ComponentLoadingBoundary>
    </Grid>
  );
}

export default VarImage;
